import * as amplitude from "@amplitude/analytics-browser";
import { ViewExportType } from "@redotech/cockroachdb-codegen/congodb/view-export-type";
import { useRequiredContext } from "@redotech/react-util/context";
import { useHandler } from "@redotech/react-util/hook";
import { FulfillmentGroupData } from "@redotech/redo-fulfillment-group-common/fulfillment-group";
import { OutboundSystemView } from "@redotech/redo-fulfillment-operations-common/views/default-outbound-views";
import { UserContext } from "@redotech/redo-merchant-app-common/user";
import { RedoOutboundLabelsRpcClient } from "@redotech/redo-merchant-fulfillment-rpc/client";
import { ViewExportScope } from "@redotech/redo-views-common/view-exports";
import { viewTypes } from "@redotech/redo-views-common/views";
import { showNotification } from "@redotech/redo-web/alert";
import { RedoTextInput } from "@redotech/redo-web/arbiter-components/input/redo-text-input";
import { RedoModal } from "@redotech/redo-web/arbiter-components/modal/redo-modal";
import { RedoRadioGroup } from "@redotech/redo-web/arbiter-components/radio/redo-radio-group";
import { Flex } from "@redotech/redo-web/flex";
import { Text } from "@redotech/redo-web/text";
import { memo, useState } from "react";
import { useNavigate } from "react-router-dom";

export interface ExportOrdersModalProps {
  open: boolean;
  onClose: () => void;
  selectedFulfillmentGroups: FulfillmentGroupData[];
  rpcClient: RedoOutboundLabelsRpcClient;
  currentView: viewTypes["fulfillment-groups"];
}

type ExportScope = "selected" | "all";
type ExportFormat = "orders" | "lineItems" | "units";

const exportTypeByFormat: Record<ExportFormat, ViewExportType> = {
  orders: ViewExportType.Standard,
  lineItems: ViewExportType.Itemized,
  units: ViewExportType.Expanded,
};

export const ExportOrdersModal = memo(function ExportOrdersModal({
  open,
  onClose,
  selectedFulfillmentGroups,
  rpcClient,
  currentView,
}: ExportOrdersModalProps) {
  const user = useRequiredContext(UserContext);
  const navigate = useNavigate();

  const [exportScope, setExportScope] = useState<ExportScope>(
    selectedFulfillmentGroups.length > 0 ? "selected" : "all",
  );
  const [exportFormat, setExportFormat] = useState<ExportFormat>("orders");
  const [sendToEmail, setSendToEmail] = useState<string>(user.email || "");

  const selectedCount = selectedFulfillmentGroups.length;

  const isValidEmail = (email: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email.trim());
  };

  const handleConfirm = useHandler(() => {
    const selectedDocumentIds =
      exportScope === "selected"
        ? selectedFulfillmentGroups.map((group) => group._id)
        : undefined;
    const eventProperties = {
      exportFormat,
      exportScope,
      selectedCount,
      viewId: currentView._id,
      viewType: new Set<string>(Object.values(OutboundSystemView)).has(
        currentView.name,
      )
        ? "system"
        : "custom",
    };
    amplitude.logEvent("oms-fulfillment-export-submitted", eventProperties);

    // Close modal immediately
    onClose();

    // Show initial notification
    showNotification({
      title: "Export requested",
      description:
        "Preparing your export... monitor progress in the downloads page.",
      primaryButton: {
        text: "View downloads",
        onClick: () => navigate("../downloads"),
      },
    });

    // Continue export in background
    rpcClient
      .createViewExport({
        view: currentView,
        exportType: exportTypeByFormat[exportFormat],
        exportScope:
          exportScope === "selected"
            ? ViewExportScope.SELECTED
            : ViewExportScope.ALL,
        selectedDocumentIds,
        sendToEmail,
      })
      .then((result) => {
        amplitude.logEvent("oms-fulfillment-export-succeeded", {
          ...eventProperties,
          deliveryMode: result.fileUrl ? "download" : "email",
        });
        if (result.fileUrl) {
          // Export completed quickly, trigger download
          const link = document.createElement("a");
          link.href = result.fileUrl;
          link.download = `fulfillment-export-${result.exportId}.csv`;
          link.style.display = "none";
          document.body.append(link);
          link.click();

          setTimeout(() => {
            link.remove();
          }, 100);

          showNotification({
            title: "Export complete",
            description: "Your export has been downloaded.",
          });
        } else {
          // Export taking longer, will be emailed
          showNotification({
            title: "Export in progress",
            description: `Your export is being processed and will be sent to ${sendToEmail} when ready.`,
            primaryButton: {
              text: "View downloads",
              onClick: () => navigate("../downloads"),
            },
          });
        }
      })
      .catch((error) => {
        amplitude.logEvent("oms-fulfillment-export-failed", eventProperties);
        console.error("Export failed:", error);
        showNotification({
          title: "Export failed",
          description: "Failed to create export. Please try again.",
          primaryButton: {
            text: "View downloads",
            onClick: () => navigate("../downloads"),
          },
        });
      });
  });

  const scopeOptions = [
    {
      id: "selected" as const,
      label: `Selected orders (${selectedCount})`,
      disabled: selectedCount === 0,
    },
    { id: "all" as const, label: "All orders in view " },
  ];

  const formatOptions = [
    {
      id: "orders" as const,
      label: "Export orders",
      description: "One row per order",
    },
    {
      id: "lineItems" as const,
      label: "Export line items",
      description: "One row per line item, with its quantity",
    },
    {
      id: "units" as const,
      label: "Export units",
      description: "One row per unit",
    },
  ];

  return (
    <RedoModal
      isOpen={open}
      modalSize="sm"
      onModalCloseRequested={onClose}
      primaryButton={{
        text: "Confirm",
        onClick: handleConfirm,
        disabled:
          (exportScope === "selected" && selectedCount === 0) ||
          !sendToEmail ||
          !isValidEmail(sendToEmail),
        size: "sm",
      }}
      secondaryButton={{ text: "Cancel", onClick: onClose, size: "sm" }}
      title="Export orders"
    >
      <Flex dir="column" gap="xl">
        <Flex dir="column" gap="md">
          <Text>Which orders would you like to export?</Text>
          <RedoRadioGroup<ExportScope>
            gap="sm"
            options={scopeOptions}
            setValue={setExportScope}
            value={exportScope}
          />
        </Flex>

        <Flex dir="column" gap="md">
          <Text>Export format</Text>
          <RedoRadioGroup<ExportFormat>
            gap="sm"
            options={formatOptions}
            setValue={setExportFormat}
            value={exportFormat}
          />
        </Flex>

        <Flex dir="column" gap="md">
          <RedoTextInput
            errorText={
              sendToEmail && !isValidEmail(sendToEmail)
                ? "Please enter a valid email address"
                : undefined
            }
            inputTypeHint="email"
            label="Email address"
            placeholder="Enter email address"
            setValue={setSendToEmail}
            value={sendToEmail}
          />
        </Flex>
      </Flex>
    </RedoModal>
  );
});
